/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { useCallback, useState } from "react";
import { useMessages } from "@epic-mw/i18n";
import { css, cx } from "@panda/css";
import { Flex } from "@panda/jsx";
import { CosmosAsset, EdsIcon, OptimizedCosmosAssetFragment } from "@components/elements/atoms";

interface AssetGalleryProps {
	assets?: OptimizedCosmosAssetFragment[] | null;
}

const galleryButton = css({
	cursor: "pointer",
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
	height: "eds.dimension.48",
	width: "eds.dimension.48",
	padding: "eds.dimension.12",
	background: "eds.fill.contrast.default",
	borderRadius: "eds.borderRadius.full",
	smDown: {
		height: "eds.dimension.24",
		width: "eds.dimension.24",
		padding: "eds.dimension.6",
	},
	"&:hover": {
		background: "eds.fill.contrast.hover",
	},
});

const galleryThumbnail = css({
	aspectRatio: "16/9",
	cursor: "pointer",
	height: "68px",
	borderRadius: "eds.borderRadius.8",
	smDown: {
		height: "eds.dimension.32",
	},
	"&:hover, &:focus": {
		outline: "2px solid white",
		padding: "2px",
		"& .asset-gallery-thumbnail": {
			opacity: "0.8",
		},
	},
	"& .cosmos-attribution": {
		display: "none",
	},
});
const galleryThumbnailAsset = css({
	height: "100%",
	width: "100%",
	objectFit: "cover",
	borderRadius: "eds.borderRadius.8",
});

export const AssetGallery = ({ assets }: AssetGalleryProps): React.JSX.Element | null => {
	const messages = useMessages();
	const [currentIndex, setCurrentIndex] = useState<number>(0);

	const handlePrevClick = useCallback((): void => {
		if (!assets?.length) return;
		setCurrentIndex((prevIndex) => (prevIndex === 0 ? assets.length - 1 : prevIndex - 1));
	}, [assets?.length, setCurrentIndex]);

	const handleNextClick = useCallback((): void => {
		if (!assets?.length) return;
		setCurrentIndex((prevIndex) => (prevIndex === assets.length - 1 ? 0 : prevIndex + 1));
	}, [assets?.length, setCurrentIndex]);

	const handleThumbnailClick = useCallback(
		(index: number): void => {
			setCurrentIndex(index);
		},
		[setCurrentIndex],
	);

	if (!assets?.length) return null;

	return (
		<Flex direction="column" gap="eds.dimension.16">
			<div
				key={`${assets[currentIndex].url}`}
				className={css({ position: "relative", width: "100%", aspectRatio: "16 / 9" })}
			>
				<CosmosAsset
					className={css({
						height: "100%",
						width: "100%",
						objectFit: "cover",
						animation: `fadein 0.3s ease-out`,
						borderRadius: "eds.borderRadius.24",
						smDown: {
							borderRadius: "eds.borderRadius.12",
						},
					})}
					asset={assets[currentIndex]}
				/>
				<Flex
					direction="row"
					justifyContent="space-between"
					className={css({
						position: "absolute",
						top: "calc(50% - 24px)",
						width: "100%",
						paddingInline: "eds.dimension.32",
						smDown: {
							top: "calc(50% - 12px)",
							paddingInline: "eds.dimension.8",
						},
					})}
				>
					<button
						className={galleryButton}
						onClick={handlePrevClick}
						aria-label={messages["epic.cosmos.view.previous"] || "View previous"}
					>
						<EdsIcon color="onAccent" size="sm" icon="ChevronLeft" />
					</button>
					<button
						className={galleryButton}
						onClick={handleNextClick}
						aria-label={messages["epic.cosmos.view.next"] || "View next"}
					>
						<EdsIcon color="onAccent" size="sm" icon="ChevronRight" />
					</button>
				</Flex>
			</div>
			<Flex justifyContent="center" wrap="wrap" gap="eds.dimension.16">
				{assets.map((asset, index) => (
					<button
						key={index}
						onClick={() => handleThumbnailClick(index)}
						aria-label={asset.altText || ""}
						className={cx(
							galleryThumbnail,
							css({
								outline: index === currentIndex ? "2px solid white" : "none",
								padding: index === currentIndex ? "2px" : "none",
							}),
						)}
					>
						<CosmosAsset
							asset={asset}
							disableAutoplay
							height={68}
							width={121}
							className={cx("asset-gallery-thumbnail", galleryThumbnailAsset)}
						/>
					</button>
				))}
			</Flex>
		</Flex>
	);
};
